import React, { useState } from 'react';
import { isWeb } from '../util/platform';
import { View, StyleSheet, Pressable, Alert } from 'react-native'
import { Feather } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';

import AppStyles from '../AppStyles';
import { formatRelativeTime } from '../util/format';

import CustomText from './CustomText';
import { useTranslation } from '../i18n';

/**
 * AttachmentList - Component for managing document attachments on reports
 *
 * @param {Array} attachments - Array of attachment objects
 * @param {Function} onUpload - Callback when uploading attachment (file) => void
 * @param {Function} onDownload - Callback when downloading attachment (attachmentId) => void
 * @param {Function} onDelete - Callback when deleting attachment (attachmentId) => void
 * @param {boolean} isAdmin - Whether the current user is an admin
 * @param {object} containerStyle - Optional style override for container
 */
const AttachmentList = ({ attachments = [], onUpload, onDownload, onDelete, isAdmin = false, containerStyle }) => {
  const { t } = useTranslation(['reports', 'common']);

  const [isUploading, setIsUploading] = useState(false);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (contentType) => {
    if (contentType.includes('pdf')) return 'file-text';
    if (contentType.includes('word') || contentType.includes('document')) return 'file';
    if (contentType.includes('excel') || contentType.includes('spreadsheet')) return 'file';
    if (contentType.includes('image')) return 'image';
    return 'file';
  };

  const handlePickDocument = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: [
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'image/*',
        ],
        copyToCacheDirectory: true,
      });

      if (result.type === 'success' || !result.canceled) {
        const file = result.assets ? result.assets[0] : result;

        // Client-side validation - 10MB limit
        const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
        if (file.size && file.size > MAX_FILE_SIZE) {
          if (isWeb()) {
            window.alert(t('attachments.tooLargeToast'));
          } else {
            const { Alert } = require('react-native');
            Alert.alert(t('attachments.tooLargeTitle'), t('attachments.tooLargeMessage'));
          }
          return;
        }

        setIsUploading(true);

        // For web, we need to read the file as base64
        let base64Data;
        if (isWeb()) {
          const response = await fetch(file.uri);
          const blob = await response.blob();
          base64Data = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
        } else {
          // For mobile, we need to convert the file to base64
          const FileSystem = require('expo-file-system');
          base64Data = await FileSystem.readAsStringAsync(file.uri, {
            encoding: FileSystem.EncodingType.Base64,
          });
          base64Data = `data:${file.mimeType};base64,${base64Data}`;
        }

        await onUpload({
          uri: file.uri,
          name: file.name,
          type: file.mimeType || file.type,
          size: file.size,
          base64: base64Data,
        });

        setIsUploading(false);
      }
    } catch (error) {
      console.error('Error picking document:', error);
      setIsUploading(false);
    }
  };

  const handleDownload = async (attachment) => {
    try {
      await onDownload(attachment.id);
    } catch (error) {
      console.error('Error downloading attachment:', error);
    }
  };

  const handleDelete = async (attachment) => {
    if (!onDelete) return;

    const doDelete = async () => {
      try {
        await onDelete(attachment.id);
      } catch (error) {
        console.error('Error deleting attachment:', error);
      }
    };

    // Show confirmation dialog
    if (isWeb()) {
      if (window.confirm(t('attachments.deleteConfirm', { filename: attachment.filename }))) {
        await doDelete();
      }
    } else {
      Alert.alert(
        t('attachments.deleteTitle'),
        t('attachments.deleteConfirm', { filename: attachment.filename }),
        [
          { text: t('common:actions.cancel'), style: 'cancel' },
          { text: t('common:actions.delete'), style: 'destructive', onPress: doDelete },
        ],
      );
    }
  };

  const renderAttachmentItem = (attachment) => {
    return (
      <Pressable
        key={attachment.id}
        style={({ hovered, focused }) => [
          styles.attachmentItem,
          { cursor: 'pointer' },
          (hovered || focused) && styles.attachmentItemHovered,
        ]}
        onPress={() => handleDownload(attachment)}
        accessibilityRole="button"
        accessibilityLabel={`Download ${attachment.filename}`}
      >
        <View style={styles.iconContainer}>
          <Feather
            name={getFileIcon(attachment.contentType)}
            size={24}
            color={AppStyles.colorSet.info}
          />
        </View>

        <View style={styles.attachmentContent}>
          <CustomText
            font="medium"
            size={14}
            numberOfLines={1}
            style={styles.filename}
            color={AppStyles.colorSet.textPrimary}
          >
            {attachment.filename}
          </CustomText>

          <View style={styles.attachmentMeta}>
            <CustomText
              font="regular"
              size={12}
              color={AppStyles.colorSet.textMuted}
            >
              {formatFileSize(attachment.size)}
            </CustomText>
            {attachment.uploadedAt && (
              <>
                <CustomText font="regular" size={12} color={AppStyles.colorSet.textMuted}>
                  {' • '}
                </CustomText>
                <CustomText font="regular" size={12} color={AppStyles.colorSet.textMuted}>
                  {formatRelativeTime(attachment.uploadedAt, 'verbose')}
                </CustomText>
              </>
            )}
          </View>
          {attachment.uploadedBy && (
            <CustomText
              font="regular"
              size={11}
              color={AppStyles.colorSet.textMuted}
              style={{ marginTop: 2 }}
            >
              {attachment.uploadedBy}
            </CustomText>
          )}
        </View>

        <View style={styles.actionButtons}>
          <Pressable
            onPress={(e) => {
              e.stopPropagation();
              handleDownload(attachment);
            }}
            accessibilityRole="button"
            accessibilityLabel={`Download ${attachment.filename}`}
            style={({ hovered, focused }) => [
              styles.iconButton,
              { cursor: 'pointer' },
              (hovered || focused) && styles.iconButtonHovered,
            ]}
          >
            <Feather name="download" size={20} color={AppStyles.colorSet.info} />
          </Pressable>

          {isAdmin && onDelete && (
            <Pressable
              onPress={(e) => {
                e.stopPropagation();
                handleDelete(attachment);
              }}
              accessibilityRole="button"
              accessibilityLabel={`Delete ${attachment.filename}`}
              style={({ hovered, focused }) => [
                styles.iconButton,
                { cursor: 'pointer' },
                (hovered || focused) && styles.iconButtonHovered,
              ]}
            >
              <Feather name="trash-2" size={20} color={AppStyles.colorSet.danger} />
            </Pressable>
          )}
        </View>
      </Pressable>
    );
  };

  return (
    <View style={[styles.container, containerStyle]}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <CustomText font="semiBold" size={18} color={AppStyles.colorSet.textPrimary}>
            {t('attachments.title')}
          </CustomText>
          <CustomText font="regular" size={14} color={AppStyles.colorSet.textSecondary}>
            {t('attachments.fileCount', { count: attachments.length })}
          </CustomText>
        </View>

        {isAdmin && (
          <Pressable
            onPress={handlePickDocument}
            disabled={isUploading}
            style={({ hovered, focused }) => [
              styles.uploadButton,
              { cursor: 'pointer' },
              !isUploading && (hovered || focused) && styles.uploadButtonHovered,
              isUploading && styles.uploadButtonDisabled,
            ]}
            accessibilityRole="button"
            accessibilityLabel={t('attachments.uploadLabel')}
          >
            <Feather
              name={isUploading ? 'loader' : 'upload'}
              size={18}
              color={isUploading ? AppStyles.colorSet.textMuted : '#000000'}
            />
            <CustomText
              font="medium"
              size={14}
              color={isUploading ? AppStyles.colorSet.textMuted : '#000000'}
              style={{ marginLeft: 6 }}
            >
              {isUploading ? t('attachments.uploading') : t('common:actions.upload')}
            </CustomText>
          </Pressable>
        )}
      </View>

      {attachments.length === 0 ? (
        <Pressable
          style={({ hovered, focused }) => [
            styles.emptyState,
            isAdmin && { cursor: 'pointer' },
            isAdmin && !isUploading && (hovered || focused) && styles.emptyStateHovered,
          ]}
          onPress={isAdmin ? handlePickDocument : null}
          disabled={isUploading || !isAdmin}
          accessibilityRole={isAdmin ? 'button' : 'none'}
          accessibilityLabel={isAdmin ? t('attachments.uploadFile') : undefined}
        >
          <Feather name="paperclip" size={40} color={AppStyles.colorSet.textMuted} />
          <CustomText
            font="medium"
            size={14}
            color={AppStyles.colorSet.textPrimary}
            style={{ marginTop: 8 }}
          >
            {t('attachments.empty')}
          </CustomText>
          <CustomText
            font="regular"
            size={12}
            color={AppStyles.colorSet.textMuted}
            style={{ marginTop: 4, textAlign: 'center', maxWidth: 280 }}
          >
            {t('attachments.emptyHint')}
          </CustomText>
          {isAdmin && (
            <CustomText
              font="medium"
              size={12}
              color={AppStyles.colorSet.info}
              style={{ marginTop: 8 }}
            >
              {t('attachments.clickToUpload')}
            </CustomText>
          )}
        </Pressable>
      ) : (
        <View style={styles.attachmentList}>
          {attachments.map((attachment) => renderAttachmentItem(attachment))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: AppStyles.spacing.md,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: AppStyles.spacing.md,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: AppStyles.spacing.sm,
  },
  uploadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: AppStyles.spacing.md,
    paddingVertical: AppStyles.spacing.sm,
    borderRadius: AppStyles.borderRadii.md,
    backgroundColor: AppStyles.colorSet.brandPrimary,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 1,
  },
  // Web hover/focus feedback — lift on the filled Upload button
  uploadButtonHovered: {
    opacity: 0.92,
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 2,
  },
  uploadButtonDisabled: {
    backgroundColor: AppStyles.colorSet.inputBackground,
    shadowOpacity: 0,
    elevation: 0,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: AppStyles.spacing.xl * 1.5,
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: AppStyles.borderRadii.md,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: AppStyles.colorSet.cardBorder,
  },
  // Web hover/focus feedback for the clickable upload dropzone
  emptyStateHovered: {
    borderColor: AppStyles.colorSet.brandPrimary,
    backgroundColor: AppStyles.stateSet.hover,
  },
  emptyText: {
    marginTop: AppStyles.spacing.sm,
  },
  attachmentList: {
    gap: AppStyles.spacing.sm,
  },
  attachmentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AppStyles.colorSet.cardBackground,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
    borderRadius: AppStyles.borderRadii.md,
    padding: AppStyles.spacing.md,
    gap: AppStyles.spacing.md,
  },
  // Web hover/focus feedback for the clickable attachment row
  attachmentItemHovered: {
    borderColor: AppStyles.colorSet.brandPrimary,
    backgroundColor: AppStyles.stateSet.hover,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: AppStyles.borderRadii.sm,
    backgroundColor: AppStyles.colorSet.infoLight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  attachmentContent: {
    flex: 1,
  },
  filename: {
    marginBottom: 4,
  },
  attachmentMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  actionButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: AppStyles.spacing.xs,
  },
  iconButton: {
    padding: AppStyles.spacing.sm,
  },
  // Web hover/focus feedback for the download/delete icon buttons
  iconButtonHovered: {
    backgroundColor: AppStyles.stateSet.hover,
    borderRadius: AppStyles.borderRadii.md,
  },
});

export default AttachmentList;
